/**
 * Turns a failed axios request into properties for a PostHog $exception.
 *
 * The client half of /server/services/errorRequestContext.js, and it exists for
 * the same reason: an AxiosError's message is "Request failed with status code
 * 500" and nothing else. Every 500 in the app therefore collapses into one
 * PostHog issue that cannot say which endpoint broke — which is exactly the
 * state we were in while /course-review/ was failing in production.
 *
 * Sanitizing matters as much here as on the server. RME puts bearer
 * capabilities directly in paths — /api/reviewer/start/:id IS the token that
 * grants access to that review — so the raw request URL must not be copied into
 * a third-party analytics store.
 */

// A Mongo ObjectId and a UUID, as on the server.
const OBJECT_ID = /^[a-f\d]{24}$/i;
const UUID = /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i;

/**
 * Route segments are words: "review_cycle", "current", "complete". Identifiers
 * are not — the review links in the wild look like "6a9ad36cbc2f57891f8e4" and
 * "Njc4ZmEzZT", neither of which is a full ObjectId, so the two exact patterns
 * above are not enough on their own.
 *
 * @param {string} segment
 * @returns {boolean} True when the segment reads as an identifier.
 */
function looksLikeIdentifier(segment) {
  if (segment.length < 8) return false;
  if (!/^[\w-]+$/.test(segment)) return false;

  const hasDigit = /\d/.test(segment);
  const hasLetter = /[a-z]/i.test(segment);
  const mixedCase = /[a-z]/.test(segment) && /[A-Z]/.test(segment);

  return (hasDigit && hasLetter) || mixedCase;
}

/**
 * Strip the query string and replace identifier segments with placeholders, so
 * issues group by endpoint instead of splintering into one per reviewer.
 *
 * @param {string} url - Request URL, absolute or relative.
 * @returns {string} A path safe to store, e.g. "/api/course/current/review_cycle/:id".
 */
export function sanitizePath(url) {
  if (typeof url !== "string" || !url) return "";

  // The whole query string goes: api_key rides there, and no other parameter is
  // worth the standing risk of allow-listing wrongly as new ones are added.
  const [withoutQuery] = url.split("?");
  const pathOnly = withoutQuery.replace(/^https?:\/\/[^/]+/i, "");

  return pathOnly
    .split("/")
    .map((segment) => {
      if (OBJECT_ID.test(segment)) return ":id";
      if (UUID.test(segment)) return ":uuid";
      if (looksLikeIdentifier(segment)) return ":id";
      return segment;
    })
    .join("/");
}

/**
 * @param {*} error - Anything caught. Non-axios errors yield null.
 * @returns {Object|null} Properties naming the endpoint that failed.
 */
export function axiosErrorContext(error) {
  const config = error?.config;
  if (!config || typeof config.url !== "string") return null;

  return {
    source: "api",
    path: sanitizePath(config.url),
    method: (config.method || "get").toUpperCase(),
    // Absent on a network error or a timeout, which is itself the signal that
    // the request never reached the server.
    status: error?.response?.status ?? null,
    code: error?.code ?? null,
  };
}
